import React from 'react';
import { Box, Typography, Stepper, Step, StepLabel, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ExpectRoiIcon from '@/assets/icons/ExpectRoiIcon';
import UserIcon from '@/assets/icons/UserIcon';
import DollarIcon from '@/assets/icons/DollarIcon';
import LockIcon from '@/assets/icons/LockIcon';
import ZeroDisruptionIcon from '@/assets/icons/ZeroDisruptionIcon';
import TrustedIcon from '@/assets/icons/TrustedIcon';

const steps = [
  { label: 'Business Onboarding', subtitle: 'Setting up your account' },
  { label: 'Agent Training Info', subtitle: 'To map your receptionist workflows' }
];

interface LeftPanelProps {
  organizationData?: any;
}

const LeftPanel: React.FC<LeftPanelProps> = ({ organizationData }) => {
  const theme = useTheme();

  return (
    <Grid
      size={{ xs: 0, md: 6 }}
      sx={{
        display: { xs: 'none', md: 'flex' },
        background: 'linear-gradient(154deg, #77B2DC 0%, #FFFFFF 49.07%, #D9F66C 99.08%);',
        p: { md: '30px', lg: '60px' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflowY: 'auto',
        maxHeight: '100%'
      }}
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            '& img': {
              height: { xs: 32, lg: 56 },
              paddingRight: { xs: 1, lg: 1.25 }
            }
          }}
        >
          <img src={organizationData?.logo_url} alt={organizationData?.name} />
        </Box>

        <Stepper
          activeStep={0}
          sx={{
            mt: 4,
            pointerEvents: 'none',

            // Circle (default / inactive)
            '& .MuiStepIcon-root': {
              color: '#fff'
            },

            // Active circle
            '& .MuiStepIcon-root.Mui-active': {
              color: theme.palette.primary.main
            },

            // Completed circle
            '& .MuiStepIcon-root.Mui-completed': {
              color: theme.palette.primary.main
            },
            '& .MuiStepIcon-root:not(.Mui-active):not(.Mui-completed) .MuiStepIcon-text': {
              fill: '#000000'
            }
          }}
        >
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel
                optional={
                  <Typography fontSize='12px' color='text.secondary'>
                    {step.subtitle}
                  </Typography>
                }
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Typography fontSize='32px' fontWeight={600} mt={4}>
          You've made the right call.
        </Typography>

        <Typography lineHeight='normal' fontSize='32px' fontWeight={600} mt={1.5}>
          Let’s build your <span style={{ color: theme.palette.primary.main }}>AI receptionist</span>.
        </Typography>

        <Typography mt={1.5} color='text.secondary'>
          A few quick steps and your AI agent will be ready to handle calls, book appointments, and delight your
          customers — 24/7.
        </Typography>

        <Box mt={3}>
          {[
            { text: 'Expect 10x ROI from day one', icon: <ExpectRoiIcon /> },
            { text: 'We map your receptionist workflow for you', icon: <UserIcon /> },
            { text: 'Your monthly subscription will start once your agent is live', icon: <DollarIcon /> },
            { text: 'Cancel anytime — no lock-in contracts', icon: <LockIcon /> },
            { text: 'Zero disruption — nothing changes on your end', icon: <ZeroDisruptionIcon /> }
          ].map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
              <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>{item.icon}</Box>
              <Typography fontWeight={500}>{item.text}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
        <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>
          <TrustedIcon />
        </Box>
        <Typography variant='body2'>Trusted by 500+ businesses across North America</Typography>
      </Box>
    </Grid>
  );
};

export default LeftPanel;
